// b12: motorn, förbrukningen och trimmen. Bara läsning på NMEA 2000.
//
// Navet sammanställer de snabba PGN:erna (10 Hz) till resor och minuter, och skickar bara
// sammandragen till molnet: aldrig råa ramar, och aldrig spåret. En resa sparar sträckan, tiden
// och förbrukningen. Molnet räknar förbrukningskurvan, den ekonomiska farten, räckvidden,
// jämförelsen av motorerna, servicen och provkörningarna ur dem.
//
// I koden heter båtens resor "färder", eftersom "resa" redan är Resa bort (b3). Appen säger Resor.
//
// b13: två oberoende sidor. En sida är en motor och en dieseltank, och räckvidden räknas per sida.
// Navet mäter tankarnas form: stabila nivåer och det som motorerna förbrukade mellan dem
// (Tankpar), och molnet bygger tabellen ur paren (tankform.ts). Motorernas egen resedata (PGN
// 127496 och 127497) visas bredvid navets.
//
// b14: båtens uppställning (uppstallning.ts) ersätter de två fasta sidorna: motorer på fem
// platser, tankar och matningen mellan dem. Räckvidden räknas per grupp av motorer och tankar som
// hänger ihop genom matningen. Värdena per motor finns för de motorer som båten har.

import { type Fynd, kontrolleraFyndlista } from "@koll/katalog";
import {
  type AUTOPILOTLAGEN,
  FORBRUKARE,
  type Forbrukarnyckel,
  GAMLA_MOTORER,
  MOTORER,
  type Motor,
} from "./signaler.ts";
import type { Gransinstallning } from "./typer.ts";
import type { Grupp, Uppstallning, Uppstallningsforslag } from "./uppstallning.ts";

/** b15: en generator har platsen generator, generator2 och så vidare. Den driver inte båten. */
export const arGenerator = (m: string): boolean => /^generator[2-4]?$/.test(m);

export type Autopilotlage = (typeof AUTOPILOTLAGEN)[number];

/**
 * Ett värde per motor. Null: motorn sänder det inte, och appen visar "finns inte". b14: bara de
 * motorer som båten har finns med, till exempel babord och styrbord, eller bara mitten.
 */
export type PerMotor<T> = Partial<Record<Motor, T | null>>;

/** Vilken instans i NMEA 2000 som är vilken motor, per plats. b14: bara motorerna med en instans. */
export type Motorinstanser = Partial<Record<Motor, number>>;

/** Förvalet: instans 0 är babord och instans 1 styrbord, som canboats ENGINE_INSTANCE. */
export const FORVALDA_INSTANSER: Motorinstanser = { babord: 0, styrbord: 1 };

/** b13: vilken dieseltank som hör till vilken sida. Förvalet: diesel babord, diesel2 styrbord. */
export type Sidtankar = Partial<Record<Motor, string>>;

export const FORVALDA_TANKAR: Sidtankar = { babord: "diesel", styrbord: "diesel2" };

/**
 * b13: en tank med bränsle: diesel, diesel2 och så vidare (tank.niva@diesel i b10). b14: också
 * bensin (canboats Fuel (Gasoline)).
 */
export const BRANSLETANK = /^(diesel|bensin)\d{0,2}$/;

/** Motorerna som finns i värdena, i platsernas ordning. */
export function motorerI(...v: (PerMotor<unknown> | undefined)[]): Motor[] {
  return MOTORER.filter((m) => v.some((x) => x !== undefined && Object.hasOwn(x, m)));
}

/** Reglerna för resorna (avsnitt B). */
export const RESA = {
  /** En resa börjar när en motor går och farten över grund är över 2 knop i minst 1 minut. */
  startKnop: 2,
  startMs: 60_000,
  /** Den slutar när båda motorerna har stått i 10 minuter. */
  stoppMs: 10 * 60_000,
} as const;

/** Reglerna för en jämn sträcka (avsnitt C). */
export const JAMN = {
  /** Sträckans längd: en minut. En lång jämn gång blir en sträcka per minut. */
  langdMs: 60_000,
  /** Varvtalet inom ±3 % av minutens medel, för varje motor som går. */
  varvProcent: 3,
  /** Farten över grund över 3 knop hela minuten. */
  minKnop: 3,
  /** Ingen stor kursändring: kursen i minutens första och sista 10 sekunder skiljer högst 15°. */
  kursandringGrader: 15,
  /** Och ingen del av minuten mer än 25° från minutens medelkurs. */
  kursavvikelseGrader: 25,
} as const;

/** Kurvan och den ekonomiska farten: ett steg per knop, och minst 5 jämna sträckor per steg. */
export const KURVA = { stegKnop: 1, minstStrackor: 5 } as const;

/** Räckvidden har en reserv på 20 %. */
export const RESERV = 0.2;

/** Jämförelsen av motorerna (avsnitt D): samma varvtal ±50 rpm, mer än 10 % i över 10 minuter. */
export const JAMFORELSE = { varvRpm: 50, procent: 10, minuter: 10 } as const;

/** Självsvängningen (avsnitt E): en jämn period på 3–20 sekunder. */
export const PENDLING = { minPeriod: 3, maxPeriod: 20 } as const;

/** Servicen: en påminnelse när så här många timmar återstår. */
export const SERVICE_SNART_TIMMAR = 20;

/** Sammandraget av en resa, som navet skickar varje minut medan den pågår, och när den slutar. */
export interface Fardsammandrag {
  /** Resans id, samma i alla sammandrag för resan. */
  id: string;
  start: number;
  /** När båda motorerna stannade. Null medan resan pågår. */
  slut: number | null;
  /** Tiden för sammandraget. */
  senast: number;
  /** Resans tid i sekunder, till slutet eller till sammandraget. */
  sekunder: number;
  /** Sträckan: farten över grund (GPS), integrerad. */
  distansNm: number;
  maxfartKn: number;
  /** Förbrukningen per motor: bränsleflödet (l/h), integrerat. */
  liter: PerMotor<number>;
  /** Tiden som motorn gick (varvtal över 0) under resan, i timmar. */
  gangtimmar: PerMotor<number>;
  /** Motorns timräknare (motor.timmar) när resan började, och senast. */
  timmarFran: PerMotor<number>;
  timmarTill: PerMotor<number>;
  /** Dieseltankens nivå i procent när resan började, och senast. Null utan tankgivare. */
  dieselFran: number | null;
  dieselTill: number | null;
  /** b13: varje dieseltanks nivå när resan började, och senast. Saknas från ett äldre nav. */
  tankar?: Record<string, { fran: number | null; till: number | null }>;
  /**
   * b13: motorernas egen förbrukning under resan, ur trippen i PGN 127497 (Trip Fuel Used): det
   * senaste värdet minus värdet när resan började. Null när motorn inte sänder den, eller när
   * trippen nollställdes under resan.
   */
  volvoLiter?: PerMotor<number>;
}

/**
 * b13: två stabila nivåer i en dieseltank, och bränsleflödet från motorerna (motor.bransle)
 * integrerat mellan dem. Navet mäter, och molnet bygger tankens tabell ur paren. En nivå är
 * stabil när båten ligger still och nivån inte svajar (tankmatare.ts i navet).
 */
export interface Tankpar {
  tank: string;
  fran: { niva: number; tid: number };
  till: { niva: number; tid: number };
  /** Litrarna per motor mellan nivåerna. Null: motorn sände inte bränsleflödet. */
  liter: PerMotor<number>;
  /**
   * b14: hur många timmar generatorn gick mellan nivåerna, enligt generator.gar (MasterBus).
   * Molnet räknar litrarna med generatorns förbrukning för en generator utan NMEA 2000.
   */
  generatorTimmar?: number;
  /**
   * b15: hur många timmar varje förbrukare med en signal gick mellan nivåerna, till exempel
   * värmaren. Molnet räknar litrarna med förbrukningen, och lär sig förbrukningen ur paren där
   * bara en förbrukare gick.
   */
  forbrukarTimmar?: Partial<Record<Forbrukarnyckel, number>>;
}

/** b15: fynden som navet hör, med katalogens utgåva i navet. */
export interface Fyndlista {
  fynd: Fynd[];
  katalog: number;
}

/** En svängning i en signal under en minut: amplituden, och perioden när den är jämn. */
export interface Svangning {
  /** Amplituden i signalens enhet (° eller °/s). */
  amplitud: number;
  /** Perioden i sekunder, när svängningen har en jämn period. Null annars. */
  period: number | null;
}

/** En minut under en resa, som navet skickar. Medelvärden, inga råa ramar. */
export interface Fardminut {
  resa: string;
  start: number;
  sekunder: number;
  /** Minuten är en jämn sträcka (JAMN). */
  jamn: boolean;
  /** Varför minuten inte är en jämn sträcka. Tom när den är det. */
  orsak: string;
  sogKn: number | null;
  distansNm: number;
  varv: PerMotor<number>;
  /** Bränsleflödets medel (l/h), och litrarna under minuten. */
  flode: PerMotor<number>;
  liter: PerMotor<number>;
  kylvatten: PerMotor<number>;
  trim: number | null;
  slagsida: number | null;
  /** Dieselns senast kända nivå i procent. */
  diesel: number | null;
  /** Autopilotens läge hela minuten, "blandat" om det ändrades, eller null om det inte hörs. */
  autopilot: Autopilotlage | "blandat" | null;
  /** Spridningen (standardavvikelsen) i kursen, slagsidan och girhastigheten. */
  kursSpridning: number | null;
  rullning: number | null;
  girSpridning: number | null;
  /** Svängningarna i kursens avvikelse, girhastigheten och slagsidan. */
  pendling: { kurs: Svangning | null; gir: Svangning | null; slagsida: Svangning | null };
}

export type Sammandragsdata =
  | { slag: "resa"; data: Fardsammandrag }
  | { slag: "minut"; data: Fardminut }
  | { slag: "tank"; data: Tankpar }
  | { slag: "fynd"; data: Fyndlista };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const LAGEN = new Set<string>([
  "standby",
  "manuell",
  "auto",
  "vind",
  "spar",
  "ingen_drift",
  "okant",
  "blandat",
]);

function talEllerNull(v: unknown, min = -1e9, max = 1e9): boolean {
  return v === null || (typeof v === "number" && Number.isFinite(v) && v >= min && v <= max);
}

function perMotor(v: unknown, min = -1e9, max = 1e9): boolean {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  const x = v as Record<string, unknown>;
  return (
    Object.keys(x).every(
      (k) =>
        (MOTORER as readonly string[]).includes(k) ||
        (GAMLA_MOTORER as readonly string[]).includes(k),
    ) && MOTORER.every((m) => talEllerNull(x[m] ?? null, min, max))
  );
}

function forbrukarTimmar(v: unknown): boolean {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  return Object.entries(v).every(
    ([k, t]) =>
      (FORBRUKARE as readonly string[]).includes(k) &&
      typeof t === "number" &&
      talEllerNull(t, 0, 10_000),
  );
}

function tanknivaer(v: unknown): boolean {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  const x = v as Record<string, unknown>;
  return (
    Object.keys(x).length <= 10 &&
    Object.entries(x).every(([k, n]) => {
      if (!BRANSLETANK.test(k) || typeof n !== "object" || n === null) return false;
      const y = n as Record<string, unknown>;
      return talEllerNull(y.fran ?? null, -10, 200) && talEllerNull(y.till ?? null, -10, 200);
    })
  );
}

function svangning(v: unknown): boolean {
  if (v === null) return true;
  if (typeof v !== "object" || Array.isArray(v)) return false;
  const s = v as Record<string, unknown>;
  return talEllerNull(s.amplitud, 0, 1e4) && s.amplitud !== null && talEllerNull(s.period, 0, 600);
}

/** Kontrollerar ett sammandrag från navet. Returnerar ett fel eller null. */
export function kontrolleraSammandrag(slag: unknown, data: unknown): string | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return "data saknas";
  const d = data as Record<string, unknown>;
  const tid = (v: unknown) => Number.isSafeInteger(v) && (v as number) > 0;
  if (slag === "resa") {
    if (typeof d.id !== "string" || !UUID.test(d.id)) return "resans id";
    if (!tid(d.start) || !tid(d.senast) || (d.slut !== null && !tid(d.slut))) return "tiderna";
    const ok =
      talEllerNull(d.sekunder, 0, 30 * 86_400) &&
      talEllerNull(d.distansNm, 0, 10_000) &&
      talEllerNull(d.maxfartKn, 0, 100) &&
      d.sekunder !== null &&
      d.distansNm !== null &&
      d.maxfartKn !== null &&
      perMotor(d.liter, 0, 100_000) &&
      perMotor(d.gangtimmar, 0, 1000) &&
      perMotor(d.timmarFran, 0, 1e6) &&
      perMotor(d.timmarTill, 0, 1e6) &&
      talEllerNull(d.dieselFran, -10, 200) &&
      talEllerNull(d.dieselTill, -10, 200) &&
      (d.tankar === undefined || tanknivaer(d.tankar)) &&
      (d.volvoLiter === undefined || perMotor(d.volvoLiter, 0, 100_000));
    return ok ? null : "resans värden";
  }
  if (slag === "tank") {
    const niva = (v: unknown) => {
      if (typeof v !== "object" || v === null) return false;
      const x = v as Record<string, unknown>;
      return talEllerNull(x.niva, -10, 200) && x.niva !== null && tid(x.tid);
    };
    if (typeof d.tank !== "string" || !BRANSLETANK.test(d.tank)) return "tanken";
    const ok =
      niva(d.fran) &&
      niva(d.till) &&
      (d.till as { tid: number }).tid > (d.fran as { tid: number }).tid &&
      perMotor(d.liter, 0, 100_000) &&
      (d.generatorTimmar === undefined || talEllerNull(d.generatorTimmar, 0, 10_000)) &&
      (d.forbrukarTimmar === undefined || forbrukarTimmar(d.forbrukarTimmar));
    return ok ? null : "parets värden";
  }
  if (slag === "fynd") return kontrolleraFyndlista(data);
  if (slag === "minut") {
    if (typeof d.resa !== "string" || !UUID.test(d.resa)) return "resans id";
    if (!tid(d.start)) return "tiden";
    const p = d.pendling as Record<string, unknown> | null;
    const ok =
      typeof d.jamn === "boolean" &&
      typeof d.orsak === "string" &&
      d.orsak.length <= 200 &&
      talEllerNull(d.sekunder, 1, 600) &&
      d.sekunder !== null &&
      talEllerNull(d.sogKn, 0, 100) &&
      talEllerNull(d.distansNm, 0, 100) &&
      d.distansNm !== null &&
      perMotor(d.varv, 0, 20_000) &&
      perMotor(d.flode, -10, 5000) &&
      perMotor(d.liter, -10, 1000) &&
      perMotor(d.kylvatten, -50, 200) &&
      talEllerNull(d.trim, -90, 90) &&
      talEllerNull(d.slagsida, -180, 180) &&
      talEllerNull(d.diesel, -10, 200) &&
      (d.autopilot === null || (typeof d.autopilot === "string" && LAGEN.has(d.autopilot))) &&
      talEllerNull(d.kursSpridning, 0, 360) &&
      talEllerNull(d.rullning, 0, 360) &&
      talEllerNull(d.girSpridning, 0, 1000) &&
      typeof p === "object" &&
      p !== null &&
      svangning(p.kurs ?? null) &&
      svangning(p.gir ?? null) &&
      svangning(p.slagsida ?? null);
    return ok ? null : "minutens värden";
  }
  return `okänt slag: ${String(slag)}`;
}

/**
 * Motorns statusbitar i PGN 127489 (canboats ENGINE_STATUS_1 och ENGINE_STATUS_2), med svensk
 * förklaring. Navet tolkar inget som motorn inte säger: det är motorns egna namn.
 */
export const MOTORSTATUS: Record<string, string> = {
  "Check Engine": "kontrollera motorn",
  "Over Temperature": "för hög temperatur",
  "Low Oil Pressure": "lågt oljetryck",
  "Low Oil Level": "låg oljenivå",
  "Low Fuel Pressure": "lågt bränsletryck",
  "Low System Voltage": "låg spänning",
  "Low Coolant Level": "låg kylvätskenivå",
  "Water Flow": "kylvattenflödet",
  "Water In Fuel": "vatten i bränslet",
  "Charge Indicator": "laddningslampan",
  "Preheat Indicator": "förvärmning",
  "High Boost Pressure": "högt laddtryck",
  "Rev Limit Exceeded": "för högt varvtal",
  "EGR System": "EGR-systemet",
  "Throttle Position Sensor": "gasreglagets givare",
  "Emergency Stop": "nödstopp",
  "Warning Level 1": "varning nivå 1",
  "Warning Level 2": "varning nivå 2",
  "Power Reduction": "effekten begränsad",
  "Maintenance Needed": "service behövs",
  "Engine Comm Error": "kommunikationsfel i motorn",
  "Sub or Secondary Throttle": "andra gasreglaget",
  "Neutral Start Protect": "startspärr, inte i neutralläge",
  "Engine Shutting Down": "motorn stängs av",
};

/**
 * Lägen som motorn säger, men som inte är varningar: förvärmningen vid start, det andra
 * gasreglaget och startspärren. De visas vid motorn, men ger ingen Varning.
 */
export const MOTORLAGEN = new Set([
  "Preheat Indicator",
  "Sub or Secondary Throttle",
  "Neutral Start Protect",
]);

/** Motorns varningar ur signalen motor.status, utan lägena. */
export function motorvarningar(status: unknown): string[] {
  if (typeof status !== "string" || status === "") return [];
  return status.split(", ").filter((n) => n !== "" && !MOTORLAGEN.has(n));
}

/** "Low Oil Pressure (lågt oljetryck)". */
export function motorstatustext(namn: string): string {
  const sv = MOTORSTATUS[namn];
  return sv ? `${namn} (${sv})` : namn;
}

// Det som molnet visar i appen.

/** En resa i appen. */
export interface Fardrad {
  id: string;
  start: number;
  slut: number | null;
  pagar: boolean;
  sekunder: number;
  distansNm: number;
  liter: PerMotor<number>;
  /** Båda motorerna, eller null om ingen motor sänder bränsleflödet. */
  literTotalt: number | null;
  literPerNm: number | null;
  literPerTimme: number | null;
  medelfartKn: number | null;
  maxfartKn: number;
  /** Motortimmarna under resan: timräknaren, eller tiden som motorn gick om räknaren saknas. */
  motortimmar: PerMotor<number>;
  /** Dieseltanken: nivån före och efter, och litrarna med tankens volym. Null utan tankgivare. */
  diesel: { fran: number; till: number; liter: number | null } | null;
  /** b13: varje dieseltank före och efter, med namnet och litrarna (tabellen eller linjärt). */
  tankar?: { tank: string; namn: string; fran: number; till: number; liter: number | null }[];
  /** b13: motorernas egen förbrukning under resan (PGN 127497), eller null. */
  volvoLiter?: PerMotor<number> | null;
}

/** b13: en motors del av ett steg i kurvan: de jämna sträckor där motorn gick. */
export interface Motorpunkt {
  antal: number;
  literPerNm: number;
  literPerTimme: number;
}

/** Ett steg i förbrukningskurvan: farten från fartKn till fartKn + 1. */
export interface Kurvpunkt {
  fartKn: number;
  /** Antalet jämna sträckor (minuter) i steget. */
  antal: number;
  literPerNm: number;
  literPerTimme: number;
  /** Trimmens och dieselns medel för stegets sträckor, för att kunna jämföra senare. */
  trim: number | null;
  diesel: number | null;
  /** b13: per motor, ur samma sträckor. Null: motorn gick inte, eller sände inte flödet. */
  motorer?: PerMotor<Motorpunkt>;
}

/** b13: den ekonomiska farten för en motor. */
export interface Motorekonomi {
  fartKn: number;
  literPerNm: number;
  antal: number;
}

export interface Forbrukningskurva {
  /** Från, i millisekunder sedan 1970. */
  fran: number;
  punkter: Kurvpunkt[];
  /** Den ekonomiska farten: lägst liter per sjömil med minst 5 jämna sträckor. Beräknad. */
  ekonomisk: Kurvpunkt | null;
  /** b13: den ekonomiska farten per motor, med minst 5 jämna sträckor för motorn. */
  ekonomiskPerMotor?: PerMotor<Motorekonomi>;
}

export interface Rackviddsfart {
  fartKn: number;
  literPerNm: number;
  /** Sjömil, med reserven 20 %. */
  nm: number;
}

/** b14: en tank i en grupps räckvidd. */
export interface Grupptank {
  tank: string;
  namn: string;
  /** Tankens volym: ägarens, eller tankgivarens förslag. Null: ingen räckvidd för gruppen. */
  tankL: number | null;
  dieselProcent: number | null;
  dieselTid: number | null;
  literKvar: number | null;
  /** Hur litrarna räknades: med tankens tabell, eller linjärt (nivån × volymen). */
  hur: "tabell" | "linjar" | null;
}

/**
 * b14: räckvidden för en grupp: litrarna kvar i gruppens tankar ÷ gruppens liter per sjömil (dess
 * motorers summa), med 20 % i reserv. b13:s sidor är två grupper.
 */
export interface Grupprackvidd {
  namn: string;
  motorer: Motor[];
  tankar: Grupptank[];
  tankL: number | null;
  literKvar: number | null;
  nu: Rackviddsfart | null;
  ekonomisk: Rackviddsfart | null;
}

export interface Rackvidd {
  /** Dieseltankens volym, ställd i appen. Null: ingen räckvidd. Två sidor: summan. */
  tankL: number | null;
  /** Den senast kända nivån (b10), och när. */
  dieselProcent: number | null;
  dieselTid: number | null;
  literKvar: number | null;
  /** Med den nuvarande farten, och med den ekonomiska farten. */
  nu: Rackviddsfart | null;
  ekonomisk: Rackviddsfart | null;
  /** Varför något saknas, eller tom. */
  text: string;
  /**
   * b14: per grupp, när båten har fler än en grupp (b13: två sidor). Båtens räckvidd (nu och
   * ekonomisk) är då den kortaste gruppens. Tom lista: en grupp, som i b12.
   */
  grupper?: Grupprackvidd[];
  /** b13: hur litrarna räknades, för en tank. */
  hur?: "tabell" | "linjar" | null;
  /** b13: motorernas egen räckvidd (PGN 127496), den senast kända, eller null. */
  volvo?: Volvorackvidd | null;
}

/** b13: det som motorerna själva säger om räckvidden (PGN 127496). */
export interface Volvorackvidd {
  nm: number | null;
  timmar: number | null;
  liter: number | null;
  tid: number;
}

/** Spridningen och förbrukningen för en provkörning, ur dess jämna sträckor. */
export interface Provkorning {
  id: string;
  etikett: string;
  start: number;
  slut: number | null;
  pagar: boolean;
  /** Antalet jämna sträckor (minuter) i provkörningen. */
  minuter: number;
  kursSpridning: number | null;
  rullning: number | null;
  girSpridning: number | null;
  literPerNm: number | null;
  fartKn: number | null;
  trim: number | null;
  skapadAv: string;
}

export interface Servicelage {
  motor: Motor;
  /** Intervallet i timmar, ställt i appen. Null: inga påminnelser. */
  intervall: number | null;
  /** Timräknaren vid den senaste kvitterade servicen. */
  senast: number | null;
  senastTid: number | null;
  /** Motorns timräknare nu, eller null om den inte hörs. */
  timmar: number | null;
  nasta: number | null;
  kvar: number | null;
  text: string;
}

/** Sidan Resor. Ägaren och läsarna ser den. */
export interface Fardvisning {
  resor: Fardrad[];
  sasong: Forbrukningskurva;
  manad: Forbrukningskurva;
  rackvidd: Rackvidd;
  provkorningar: Provkorning[];
  service: Servicelage[];
  /** Ägaren kan starta och stoppa provkörningar och kvittera servicen. */
  kanAndra: boolean;
  /** b13: dieseltankarna, för tankningen. */
  tankar?: Dieseltank[];
}

/** En minut på sidan för en resa. */
export interface Fardminutrad {
  start: number;
  sogKn: number | null;
  literPerNm: number | null;
  varv: PerMotor<number>;
  trim: number | null;
  jamn: boolean;
  orsak: string;
  autopilot: string | null;
}

/** Sidan för en resa. */
export interface Fardsida {
  resa: Fardrad;
  minuter: Fardminutrad[];
  /** Info om resan: självsvängningen och jämförelsen av motorerna. */
  info: { tid: number; text: string }[];
}

/** Kortet Resor på Hälsa. */
export interface Fardkort {
  senaste: Fardrad[];
  ekonomisk: Kurvpunkt | null;
  rackvidd: Rackvidd;
  service: Servicelage[];
  provkorning: Provkorning | null;
}

/**
 * b13: tankens tabell, nivån i procent mot liter, inlärd ur förbrukningen (tankform.ts). En punkt
 * per 5 procentenheter.
 */
export interface Tanktabell {
  /** Litrarna vid 0, 5, …, 100 %: 21 värden. Null utan tankens volym. */
  liter: number[] | null;
  /** Liter per procentenhet i stegen 0–5, 5–10, …, 95–100 %: 20 värden. */
  lutning: number[];
  /** Stegen som har data: 20 värden. */
  inlart: boolean[];
  /** Andelen steg med data, 0–1. */
  andel: number;
  /** Tabellen används för litrarna kvar. Annars räknas de linjärt, och texten säger varför. */
  anvands: boolean;
  /** Antalet par som tabellen bygger på, och de som inte användes. */
  par: number;
  bortvalda: number;
  text: string;
}

/** b13: en dieseltank, med volymen, nivån, litrarna och tabellen. b14: också bensin. */
export interface Dieseltank {
  /** Tankens instans: diesel, diesel2. */
  instans: string;
  /** b14: tankens namn i båtens uppställning, till exempel Babords tank. */
  namn: string;
  /** b14: motorerna som tar från tanken, enligt matningen. */
  motorer: Motor[];
  /** Volymen som ägaren har ställt in, eller null. */
  volymL: number | null;
  /** Tankgivarens volym (Capacity i PGN 127505), som förslag, eller null. */
  kapacitetL: number | null;
  /** Volymen som används: ägarens, annars tankgivarens. */
  anvandVolymL: number | null;
  niva: number | null;
  tid: number | null;
  liter: number | null;
  hur: "tabell" | "linjar" | null;
  tabell: Tanktabell;
  /** När tabellen senast nollställdes, eller null. */
  nollstalld: number | null;
}

/**
 * b15: förbrukningen som navet har lärt sig: medianen av mätningarna, där bara den här
 * förbrukaren gick mellan två stabila nivåer och inga motorer.
 */
export interface Inlardforbrukning {
  lh: number;
  matningar: number;
  /** Förbrukningen föreslås först med så här många mätningar (FORBRUKNING.minstMatningar). */
  foreslas: boolean;
}

/** b15: en förbrukare som inte driver båten, med det som molnet vet om den. */
export interface Forbrukarlage {
  /** Generatorns plats (generator), eller värmarens nyckel (varmare). */
  nyckel: string;
  sort: "generator" | "varmare";
  namn: string;
  lh: number | null;
  /** Tanken som förbrukaren tar från, eller null. */
  tank: string | null;
  /** Signalen för när den går. Null: ingen, och då räknas förbrukaren inte. */
  signal: string | null;
  inlard: Inlardforbrukning | null;
  /** Förvalet ur katalogen, som förslag: till exempel 2 l/h för en generator. */
  forval: { lh: number; text: string } | null;
}

/** b15: reglerna för den inlärda förbrukningen. */
export const FORBRUKNING = {
  /** Förbrukaren ska ha gått minst så här länge mellan nivåerna (timmar). */
  minstTimmar: 1,
  /** Nivån ska ha sjunkit minst så här mycket (liter), så att svajet väger lite. */
  minstLiter: 0.5,
  /** En motor räknas som stilla när den har förbrukat högst så här mycket (liter). */
  motorLiter: 0.2,
  /** En annan förbrukare räknas som stilla när den har gått högst så här länge (timmar). */
  annanTimmar: 0.05,
  /** Förbrukningen föreslås med minst så här många mätningar. */
  minstMatningar: 3,
  /** Medianen räknas ur de senaste så här många mätningarna. */
  senaste: 10,
  /** En mätning utanför de här gränserna (l/h) används inte. */
  lagstLh: 0.05,
  hogstLh: 50,
} as const;

/** Inställningarna för motorerna och förbrukningen, för en båt. */
export interface Motorinstallningar {
  /** Motorernas instanser, ur uppställningen. */
  instanser: Motorinstanser;
  /** Volymen för tanken som babord tar från, som dieseltankens volym i b12. */
  dieseltankL: number | null;
  /** b14: båtens uppställning: motorerna, tankarna och matningen. */
  uppstallning: Uppstallning;
  /** b14: grupperna ur matningen. */
  grupper: Grupp[];
  /** b14: förslaget ur det som navet hör, med mallen. */
  forslag: Uppstallningsforslag;
  /** b13: tankarna med bränsle: uppställningens, och de som tankgivarna har sagt. */
  tankar: Dieseltank[];
  service: PerMotor<{ intervall: number | null; senast: number | null }>;
  /** b15: förbrukarna som inte driver båten: generatorerna och värmarna. */
  forbrukare: Forbrukarlage[];
  /** b15: signalerna som säger att en förbrukare går, och som platsen har hört. */
  gangsignaler: { id: string; namn: string }[];
  /** Gränserna för självsvängningen (avsnitt E). */
  pendling: {
    kursGrader: Gransinstallning;
    girGraderS: Gransinstallning;
    slagsidaGrader: Gransinstallning;
  };
}
